import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldGroup, FieldLabel, FieldLegend, FieldSet } from '@/components/ui/field';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectGroup, SelectItem } from '@/components/ui/select';
export interface PostpartumCourseConfiguration {
  enabled: boolean;
  clinically_reviewed: boolean;
  citation: string;
  follow_ups: {
    id: string;
    label: string;
    subject: 'DYAD' | 'MOTHER' | 'NEWBORN';
    anchor: 'BIRTH' | 'DISCHARGE';
    min_hours: number;
    max_hours: number;
    kind: 'VISIT' | 'CCHD' | 'METABOLIC' | 'HEARING';
  }[];
  unsigned_after_hours: number;
  epds_total_threshold?: number | null;
}
export function PostpartumCourseConfigEditor({
  value,
  onChange,
}: {
  value?: PostpartumCourseConfiguration | null;
  onChange: (value: PostpartumCourseConfiguration) => void;
}) {
  const current = value ?? {
    enabled: false,
    clinically_reviewed: false,
    citation: '',
    follow_ups: [],
    unsigned_after_hours: 24,
    epds_total_threshold: null,
  };
  const change = (patch: Partial<PostpartumCourseConfiguration>) =>
    onChange({ ...current, ...patch, clinically_reviewed: false });
  return (
    <FieldSet>
      <FieldLegend>Postpartum course protocols</FieldLegend>
      <p className="text-sm text-muted-foreground">
        Configure reviewed follow-up and screening windows. These create due tasks; booking requires a separate reviewed
        action. No clinical schedule or screening threshold is enabled automatically.
      </p>
      <Field orientation="horizontal">
        <Checkbox id="pp-enabled" checked={current.enabled} onCheckedChange={(v) => change({ enabled: v === true })} />
        <FieldLabel htmlFor="pp-enabled">Enable postpartum due tasks</FieldLabel>
      </Field>
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="pp-citation">Protocol source / jurisdiction review</FieldLabel>
          <Input
            id="pp-citation"
            className="min-h-11"
            value={current.citation}
            onChange={(e) => change({ citation: e.target.value })}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="pp-unsigned">Unsigned-entry reminder interval (hours)</FieldLabel>
          <Input
            id="pp-unsigned"
            className="min-h-11"
            type="number"
            min={1}
            value={current.unsigned_after_hours}
            onChange={(e) => change({ unsigned_after_hours: Number(e.target.value) })}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="pp-epds">Reviewed EPDS total threshold (optional)</FieldLabel>
          <Input
            id="pp-epds"
            className="min-h-11"
            type="number"
            min={1}
            max={30}
            value={current.epds_total_threshold ?? ''}
            onChange={(e) => change({ epds_total_threshold: e.target.value === '' ? null : Number(e.target.value) })}
          />
        </Field>
        {current.follow_ups.map((rule, index) => {
          const update = (patch: Partial<typeof rule>) =>
            change({ follow_ups: current.follow_ups.map((r, i) => (i === index ? { ...r, ...patch } : r)) });
          return (
            <FieldSet key={index}>
              <FieldLegend>{rule.label || `Follow-up ${index + 1}`}</FieldLegend>
              <FieldGroup>
                {(['id', 'label', 'min_hours', 'max_hours'] as const).map((key) => (
                  <Field key={key}>
                    <FieldLabel htmlFor={`pp-${index}-${key}`}>
                      {key === 'id'
                        ? 'Stable identifier'
                        : key === 'label'
                          ? 'Label'
                          : key === 'min_hours'
                            ? 'Window starts (hours after anchor)'
                            : 'Window ends (hours after anchor)'}
                    </FieldLabel>
                    <Input
                      id={`pp-${index}-${key}`}
                      className="min-h-11"
                      type={key.endsWith('hours') ? 'number' : 'text'}
                      min={0}
                      value={rule[key]}
                      onChange={(e) =>
                        update({ [key]: key.endsWith('hours') ? Number(e.target.value) : e.target.value })
                      }
                    />
                  </Field>
                ))}
                {(['subject', 'anchor', 'kind'] as const).map((key) => (
                  <Field key={key}>
                    <FieldLabel>{key}</FieldLabel>
                    <Select value={rule[key]} onValueChange={(v) => update({ [key]: v })}>
                      <SelectTrigger className="min-h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {(key === 'subject'
                            ? ['DYAD', 'MOTHER', 'NEWBORN']
                            : key === 'anchor'
                              ? ['BIRTH', 'DISCHARGE']
                              : ['VISIT', 'CCHD', 'METABOLIC', 'HEARING']
                          ).map((v) => (
                            <SelectItem key={v} value={v}>
                              {v}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </Field>
                ))}
              </FieldGroup>
              <Button
                type="button"
                variant="outline"
                className="min-h-11 self-start"
                onClick={() => change({ follow_ups: current.follow_ups.filter((_, i) => i !== index) })}>
                Remove follow-up
              </Button>
            </FieldSet>
          );
        })}
      </FieldGroup>
      <Button
        type="button"
        variant="outline"
        className="min-h-11 self-start"
        onClick={() =>
          change({
            follow_ups: [
              ...current.follow_ups,
              { id: '', label: '', subject: 'DYAD', anchor: 'DISCHARGE', kind: 'VISIT', min_hours: 0, max_hours: 0 },
            ],
          })
        }>
        Add reviewed follow-up rule
      </Button>
      <Field orientation="horizontal">
        <Checkbox
          id="pp-reviewed"
          checked={current.clinically_reviewed}
          onCheckedChange={(v) => onChange({ ...current, clinically_reviewed: v === true })}
        />
        <FieldLabel htmlFor="pp-reviewed">
          I reviewed these scheduling windows and screening criteria for this practice.
        </FieldLabel>
      </Field>
    </FieldSet>
  );
}
